import { faThumbsDown, faThumbsUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { AiRelatedCompany, Feedback } from 'bundles/connect/types';
import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, OverlayTrigger, Row, Spinner, Toast, Tooltip } from 'react-bootstrap-5';
import { connect, useSelector } from 'react-redux';
import { selectCallConsolePropertyId } from '../store/selectors/call_console';
import { selectOffMarketPropertyId } from '../store/selectors/off_market_property';
import {
  updateCallConsoleAiCompanyFeedback, updateCallConsoleOwnershipFeedback, updateOffMarketAiCompanyFeedback,
  updateOffMarketOwnershipFeedback
} from '../store/actions/ai_related_companies';
import { createFeedback, updateFeedback } from '../../helpers/prospect_api';

type Props = {
  fromConsole: boolean;
  obj: AiRelatedCompany | any;
  objType: "Connect::AiRelatedCompany" | "Connect::Ownership";
  updateCallConsoleAiCompanyFeedback: (id: string, feedback: Feedback) => void;
  updateCallConsoleOwnershipFeedback: (id: string, feedback: Feedback) => void;
  updateOffMarketAiCompanyFeedback: (id: string, feedback: Feedback) => void;
  updateOffMarketOwnershipFeedback: (id: string, feedback: Feedback) => void;
}

function AiFeedback(
  {
    fromConsole,
    obj,
    objType,
    updateCallConsoleAiCompanyFeedback,
    updateCallConsoleOwnershipFeedback,
    updateOffMarketAiCompanyFeedback,
    updateOffMarketOwnershipFeedback
  }: Props
) {
  const propertyId = useSelector(
    fromConsole ? selectCallConsolePropertyId : selectOffMarketPropertyId
  );

  const [showToast, setShowToast] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState<string>('');

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [showToast]);

  const handleAddCommentClicked = () => {
    setShowToast(false);
    setShowModal(true);
  };

  const handleSubmitComment = () => {
    setSaving(true);
    updateFeedback(
      obj.feedback.id,
      { feedback: { comment } }
    ).then(() => {
      setSaving(false);
      setShowModal(false);
    });
  };

  const handleThumbFeedback = (rating: number) => {
    if (obj.feedback) return;

    createFeedback({
      insightsPropertyId: propertyId,
      feedbackableType: objType,
      feedbackableId: obj.id,
      feedback: {
        rating
      }
    }).then((res) => {
      if (fromConsole) {
        if (objType === 'Connect::AiRelatedCompany') {
          updateCallConsoleAiCompanyFeedback(obj.id, res);
        } else if (objType === 'Connect::Ownership') {
          updateCallConsoleOwnershipFeedback(obj.id, res);
        }
      } else {
        if (objType === 'Connect::AiRelatedCompany') {
          updateOffMarketAiCompanyFeedback(obj.id, res);
        } else if (objType === 'Connect::Ownership') {
          updateOffMarketOwnershipFeedback(obj.id, res);
        }
      }
      setShowToast(true);
    });
  };

  const renderTooltipUp = props => (
    <Tooltip {...props}>Good Response</Tooltip>
  );

  const renderTooltipDown = props => (
    <Tooltip {...props}>Bad Response</Tooltip>
  );

  return (
    <>
      <Row className="align-items-center">
        <Col className="fs-x-small pe-0" xs="auto">
          AI Feedback:
        </Col>
        <Col className="ps-2">
          <OverlayTrigger offset={[0, 5]} overlay={renderTooltipUp} placement="top">
            <FontAwesomeIcon
              className={`clickable me-1 ${obj.feedback?.rating === 0 ? 'text-muted' : 'text-primary'}`}
              icon={faThumbsUp}
              id="thumbs_up_ai_feedback"
              size="sm"
              onClick={() => handleThumbFeedback(1)}
            />
          </OverlayTrigger>
          <OverlayTrigger offset={[0, 5]} overlay={renderTooltipDown} placement="top">
            <FontAwesomeIcon
              className={`clickable ${obj.feedback?.rating === 1 ? 'text-muted' : 'text-primary'}`}
              icon={faThumbsDown}
              id="thumbs_down_ai_feedback"
              size="sm"
              onClick={() => handleThumbFeedback(0)}
            />
          </OverlayTrigger>
        </Col>
      </Row>
      <div
        style={{
          position: 'fixed',
          bottom: '1rem',
          left: '1rem',
          zIndex: 1050,
          maxWidth: '600px',
        }}
      >
        <Toast className="w-100" show={showToast} onClose={() => setShowToast(false)}>
          <Toast.Header>
            <FontAwesomeIcon className="text-primary" icon={faThumbsUp} />
            <div className="fs-small fw-bolder me-auto">Feedback Recorded</div>
          </Toast.Header>
          <Toast.Body>
            <div className="d-flex align-items-center justify-content-between gap-2">
              <div>
                Thank you for letting us know about the response. Feel free to send more more information if you'd like.
              </div>
              <div className="d-flex gap-2">
                <Button
                  className="p-0 text-decoration-none text-primary"
                  variant="link"
                  onClick={() => setShowToast(false)}
                >
                  Dismiss
                </Button>
                <Button
                  className="text-nowrap"
                  variant="secondary"
                  onClick={handleAddCommentClicked}
                >
                  Add Comments
                </Button>
              </div>
            </div>
          </Toast.Body>
        </Toast>
      </div>
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header className="flex-row" closeButton>
          <Modal.Title as="h5">AI Feedback Comments</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Thank you for providing feedback. Your feedback will be used to continue improving our AI Search.
          <Form.Group>
            <Form.Label>Comments</Form.Label>
            <Form.Control
              as="textarea"
              maxLength={300}
              rows={5}
              value={comment}
              onChange={e => setComment(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex align-items-center justify-content-end gap-2">
            <Button
              className="p-0 text-decoration-none text-primary"
              variant="link"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </Button>
            <Button
              className="text-nowrap"
              disabled={!comment && !saving}
              variant="secondary"
              onClick={handleSubmitComment}
            >
              {saving && <Spinner animation="border" size="sm" />}
              Submit
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default connect(
  undefined,
  {
    updateCallConsoleAiCompanyFeedback,
    updateCallConsoleOwnershipFeedback,
    updateOffMarketAiCompanyFeedback,
    updateOffMarketOwnershipFeedback
  }
)(AiFeedback);
